import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// Historical average annual US CPI from 1913 to present (simplified but representative)
// Values are approximate annual average CPI-U.
const CPI_ANNUAL = {
  1913: 9.9, 1914: 10.0, 1915: 10.1, 1916: 10.9, 1917: 12.8, 1918: 15.1, 1919: 17.3, 1920: 20.0,
  1921: 17.9, 1922: 16.8, 1923: 17.1, 1924: 17.1, 1925: 17.5, 1926: 17.7, 1927: 17.4, 1928: 17.2,
  1929: 17.2, 1930: 16.7, 1931: 15.2, 1932: 13.6, 1933: 12.9, 1934: 13.4, 1935: 13.7, 1936: 13.9,
  1937: 14.4, 1938: 14.1, 1939: 13.9, 1940: 14.0, 1941: 14.7, 1942: 16.3, 1943: 17.3, 1944: 17.6,
  1945: 18.0, 1946: 19.5, 1947: 22.3, 1948: 24.0, 1949: 23.8, 1950: 24.1, 1951: 26.0, 1952: 26.6,
  1953: 26.8, 1954: 26.9, 1955: 26.8, 1956: 27.2, 1957: 28.1, 1958: 28.9, 1959: 29.2, 1960: 29.6,
  1961: 29.9, 1962: 30.3, 1963: 30.6, 1964: 31.0, 1965: 31.5, 1966: 32.5, 1967: 33.4, 1968: 34.8,
  1969: 36.7, 1970: 38.8, 1971: 40.5, 1972: 41.8, 1973: 44.4, 1974: 49.3, 1975: 53.8, 1976: 56.9,
  1977: 60.6, 1978: 65.2, 1979: 72.6, 1980: 82.4, 1981: 90.9, 1982: 96.5, 1983: 99.6, 1984: 103.9,
  1985: 107.6, 1986: 109.6, 1987: 113.6, 1988: 118.3, 1989: 124.0, 1990: 130.7, 1991: 136.2,
  1992: 140.3, 1993: 144.5, 1994: 148.2, 1995: 152.4, 1996: 156.9, 1997: 160.5, 1998: 163.0,
  1999: 166.6, 2000: 172.2, 2001: 177.1, 2002: 179.9, 2003: 184.0, 2004: 188.9, 2005: 195.3,
  2006: 201.6, 2007: 207.3, 2008: 215.3, 2009: 214.5, 2010: 218.1, 2011: 224.9, 2012: 229.6,
  2013: 233.0, 2014: 236.7, 2015: 237.0, 2016: 240.0, 2017: 245.1, 2018: 251.1, 2019: 255.7,
  2020: 258.8, 2021: 271.0, 2022: 292.7, 2023: 304.7, 2024: 313.6, 2025: 320.8, 2026: 328.0
};
const YEARS = Object.keys(CPI_ANNUAL).map(Number).sort((a,b)=>a-b);
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// Approx monthly using linear interp within a year for the 'Month' option
function cpiFor(year, month) {
  if (month === 'Average') return CPI_ANNUAL[year];
  const idx = MONTHS.indexOf(month);
  const prev = CPI_ANNUAL[year];
  const next = CPI_ANNUAL[year + 1] || prev;
  return prev + (next - prev) * (idx / 12);
}

export default function InflationCalc() {
  // Module 1: CPI-based
  const [cpiAmt, setCpiAmt] = useState(100);
  const [cpiFromMonth, setCpiFromMonth] = useState('Average');
  const [cpiFromYear, setCpiFromYear] = useState(2016);
  const [cpiToMonth, setCpiToMonth] = useState('June');
  const [cpiToYear, setCpiToYear] = useState(2026);
  const cpiResult = useMemo(() => {
    const cFrom = cpiFor(cpiFromYear, cpiFromMonth);
    const cTo = cpiFor(cpiToYear, cpiToMonth);
    if (!cFrom || !cTo) return 0;
    return cpiAmt * cTo / cFrom;
  }, [cpiAmt, cpiFromMonth, cpiFromYear, cpiToMonth, cpiToYear]);

  // Module 2: Forward flat rate
  const [fwdAmt, setFwdAmt] = useState(100);
  const [fwdRate, setFwdRate] = useState(3);
  const [fwdYears, setFwdYears] = useState(10);
  const fwdResult = useMemo(() => fwdAmt * Math.pow(1 + fwdRate/100, fwdYears), [fwdAmt, fwdRate, fwdYears]);

  // Module 3: Backward flat rate
  const [bwdAmt, setBwdAmt] = useState(100);
  const [bwdRate, setBwdRate] = useState(3);
  const [bwdYears, setBwdYears] = useState(10);
  const bwdResult = useMemo(() => bwdAmt / Math.pow(1 + bwdRate/100, bwdYears), [bwdAmt, bwdRate, bwdYears]);

  const monthOpts = ['Average', ...MONTHS];

  return (
    <CalcShell look="cnet" title="Inflation Calculator"
      subtitle="Adjust dollar amounts for inflation using CPI or a flat rate, over any period."
      about={<>
        <p>The <b>Inflation Calculator</b> shows how the purchasing power of money changes over time. Use the CPI-based module for historical U.S. figures, or the forward/backward flat-rate modules to project scenarios at any inflation rate.</p>
      </>}
      slug="inflation"
    >
      {/* Module 1: CPI */}
      <section className="mb-8">
        <h3 className="text-lg font-bold text-slate-900 mb-2">Inflation Calculator with U.S. CPI Data</h3>
        <p className="text-sm text-slate-600 mb-4">
          Calculates the equivalent value of the U.S. dollar in any month from 1913 to {YEARS[YEARS.length-1]}.
          Calculations are based on the average Consumer Price Index (CPI) data for all urban consumers in the U.S.
        </p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-500">$</span>
          <input type="number" value={cpiAmt} onChange={e=>setCpiAmt(+e.target.value||0)} className="w-24 px-2 py-1 border rounded" data-testid="inf-cpi-amt"/>
          <span>in</span>
          <select value={cpiFromMonth} onChange={e=>setCpiFromMonth(e.target.value)} className="px-2 py-1 border rounded">
            {monthOpts.map(m=><option key={m}>{m}</option>)}
          </select>
          <select value={cpiFromYear} onChange={e=>setCpiFromYear(+e.target.value)} className="px-2 py-1 border rounded">
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <span>=</span>
          <span className="font-bold text-blue-800" data-testid="inf-cpi-result">${cpiResult.toLocaleString(undefined,{maximumFractionDigits:2})}</span>
          <span>in</span>
          <select value={cpiToMonth} onChange={e=>setCpiToMonth(e.target.value)} className="px-2 py-1 border rounded">
            {monthOpts.map(m=><option key={m}>{m}</option>)}
          </select>
          <select value={cpiToYear} onChange={e=>setCpiToYear(+e.target.value)} className="px-2 py-1 border rounded">
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </section>

      {/* Module 2: Forward flat rate */}
      <section className="mb-8 border-t pt-6">
        <h3 className="text-lg font-bold text-slate-900 mb-2">Forward Flat Rate Inflation Calculator</h3>
        <p className="text-sm text-slate-600 mb-4">Calculates an inflation based on a certain average inflation rate after some years.</p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-500">$</span>
          <input type="number" value={fwdAmt} onChange={e=>setFwdAmt(+e.target.value||0)} className="w-24 px-2 py-1 border rounded" data-testid="inf-fwd-amt"/>
          <span>with inflation rate</span>
          <input type="number" step="0.01" value={fwdRate} onChange={e=>setFwdRate(+e.target.value||0)} className="w-20 px-2 py-1 border rounded"/>
          <span>% after</span>
          <input type="number" value={fwdYears} onChange={e=>setFwdYears(+e.target.value||0)} className="w-20 px-2 py-1 border rounded"/>
          <span>years =</span>
          <span className="font-bold text-blue-800" data-testid="inf-fwd-result">${fwdResult.toLocaleString(undefined,{maximumFractionDigits:2})}</span>
        </div>
      </section>

      {/* Module 3: Backward flat rate */}
      <section className="border-t pt-6">
        <h3 className="text-lg font-bold text-slate-900 mb-2">Backward Flat Rate Inflation Calculator</h3>
        <p className="text-sm text-slate-600 mb-4">Calculates the equivalent purchasing power of an amount some years ago based on a certain average inflation rate.</p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-500">$</span>
          <input type="number" value={bwdAmt} onChange={e=>setBwdAmt(+e.target.value||0)} className="w-24 px-2 py-1 border rounded" data-testid="inf-bwd-amt"/>
          <span>with inflation rate</span>
          <input type="number" step="0.01" value={bwdRate} onChange={e=>setBwdRate(+e.target.value||0)} className="w-20 px-2 py-1 border rounded"/>
          <span>% =</span>
          <span className="font-bold text-blue-800" data-testid="inf-bwd-result">${bwdResult.toLocaleString(undefined,{maximumFractionDigits:2})}</span>
          <input type="number" value={bwdYears} onChange={e=>setBwdYears(+e.target.value||0)} className="w-20 px-2 py-1 border rounded"/>
          <span>years ago</span>
        </div>
      </section>

      <StepsPanel steps={[
        `CPI: value_now = value_then × CPI_now / CPI_then`,
        `Forward: FV = PV × (1 + rate)^years`,
        `Backward: PV_then = PV_now ÷ (1 + rate)^years`
      ]} />

      {/* Annual U.S. inflation rate table */}
      <section className="border-t pt-6 mt-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">U.S. Inflation Rate — Year Over Year (CPI-U)</h3>
        <p className="text-sm text-slate-600 mb-3">Historical annual inflation rate computed from average U.S. CPI-U data. Latest year: {YEARS[YEARS.length-1]}.</p>
        <div className="max-h-96 overflow-auto border border-slate-200 rounded">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white sticky top-0"><tr>
              <th className="px-3 py-2 text-left">Year</th>
              <th className="px-3 py-2 text-right">Avg CPI</th>
              <th className="px-3 py-2 text-right">Inflation Rate</th>
            </tr></thead>
            <tbody>
              {[...YEARS].reverse().map((y, i) => {
                const prev = CPI_ANNUAL[y - 1];
                const cur = CPI_ANNUAL[y];
                const rate = prev ? ((cur - prev) / prev) * 100 : null;
                return (
                  <tr key={y} className={i % 2 ? 'bg-slate-50' : ''}>
                    <td className="px-3 py-1.5">{y}</td>
                    <td className="px-3 py-1.5 text-right">{cur?.toFixed(1)}</td>
                    <td className={`px-3 py-1.5 text-right font-semibold ${rate === null ? '' : rate >= 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {rate === null ? '—' : `${rate.toFixed(2)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </CalcShell>
  );
}
